import { CheckCircle, Close } from '@mui/icons-material';
import {
  Alert,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  athenaApi,
  useDeleteApiAttachmentsByIdMutation,
} from '../../../../../app/athenaApiGen';
import { RootState } from '../../../../../app/store';
import { useDeliverableModalContext } from '../DeliverableModalContext';
import { formatFileSize } from '../formatFileSize';

const ReviewStep = () => {
  const { uploadedFiles, setUploadedFiles, savedFiles, setSavedFiles } =
    useDeliverableModalContext();
  const { isEditModalOpen } = useSelector(
    (state: RootState) => state.deliverables,
  );
  const dispatch = useDispatch();
  const [deleteAttachment] = useDeleteApiAttachmentsByIdMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const allAttachments = [
    ...savedFiles.map((f) => ({
      name: f.name ?? '',
      key: f.attachmentId ?? '',
      origin: 'saved' as const,
      attachmentId: f.attachmentId ?? '',
      sizeBytes: f.fileSize ?? 0,
    })),
    ...uploadedFiles.map((f, i) => ({
      name: f.name,
      key: `upload-${i}`,
      origin: 'uploaded' as const,
      uploadedIndex: i,
      sizeBytes: f.size,
    })),
  ];

  const removeSaved = async (attachmentId: string) => {
    try {
      await deleteAttachment({ id: attachmentId }).unwrap();
      setSavedFiles((prev) =>
        prev.filter((f) => f.attachmentId !== attachmentId),
      );
      dispatch(athenaApi.util.invalidateTags(['Deliverables']));
    } catch (err) {
      console.error('Remove attachment failed:', err);
      setErrorMessage('Failed to remove attachment. Please try again.');
    }
  };

  const removeUploaded = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        overflow: 'hidden',
        gap: 2,
        padding: 1,
      }}
    >
      {/* Attachments */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Attachments ({allAttachments.length})
        </Typography>
        {allAttachments.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'auto',
              flex: '1 1 0',
              gap: 0.5,
            }}
          >
            {allAttachments.map((file) => (
              <Box
                key={file.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.75,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                </Box>
                {formatFileSize(file.sizeBytes) && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                  >
                    {formatFileSize(file.sizeBytes)}
                  </Typography>
                )}
                <Tooltip title="Remove attachment">
                  <IconButton
                    size="small"
                    onClick={() =>
                      file.origin === 'saved'
                        ? removeSaved(file.attachmentId)
                        : removeUploaded(file.uploadedIndex)
                    }
                    sx={{ color: 'text.secondary' }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
          >
            No files attached
          </Typography>
        )}
        <Snackbar
          open={errorMessage !== null}
          autoHideDuration={6000}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            onClose={() => setErrorMessage(null)}
            variant="filled"
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          backgroundColor: 'success.lighter',
          borderRadius: 1,
          border: 1,
          borderColor: 'success.light',
        }}
      >
        <CheckCircle sx={{ color: 'success.main', fontSize: 32 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {`Please review the summary before ${isEditModalOpen ? 'updating' : 'creating'} the deliverable.`}
        </Typography>
      </Box>
    </Box>
  );
};

export default ReviewStep;
